import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { EntityThumbnail } from '../components/EntityImage';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Boxes, Plus, Minus } from 'lucide-react';
import { toast } from 'sonner';

const COMMON_CALIBERS = ['9mm', '.45 ACP', '.22 LR', '5.56 NATO', '.308 WIN', '12 Gauge', '.40 S&W', '.380 ACP', '7.62x39', '.223 REM'];
const AMMO_TYPES = ['FMJ', 'JHP', 'HP', 'SP', 'Buckshot', 'Slug', 'Birdshot', 'Match', 'Tracer', 'Other'];

export default function AmmunitionPage() {
  const navigate = useNavigate();
  const [ammunition, setAmmunition] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [caliberFilter, setCaliberFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [adjusting, setAdjusting] = useState(null);

  const fetchAmmunition = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (caliberFilter) params.append('caliber', caliberFilter);
      if (typeFilter) params.append('type', typeFilter);

      const response = await api.get(`/ammunition?${params.toString()}`);
      setAmmunition(response.data);
    } catch (error) {
      console.error('Error fetching ammunition:', error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, caliberFilter, typeFilter]);

  useEffect(() => {
    fetchAmmunition();
  }, [fetchAmmunition]);

  const adjustRounds = async (ammoId, amount) => {
    setAdjusting(ammoId);
    try {
      await api.post(`/ammunition/${ammoId}/adjust`, { amount });
      // Update local state
      setAmmunition(prev => prev.map(a => 
        a.id === ammoId ? { ...a, rounds: Math.max(0, a.rounds + amount) } : a
      ));
      toast.success(`${amount > 0 ? 'Added' : 'Removed'} ${Math.abs(amount)} rounds`);
    } catch (error) {
      console.error('Error adjusting ammunition:', error);
      toast.error('Failed to adjust rounds');
    } finally {
      setAdjusting(null);
    }
  };

  const clearFilters = () => {
    setCaliberFilter('');
    setTypeFilter('');
    setSearchQuery('');
  };

  const hasActiveFilters = caliberFilter || typeFilter;

  // Get unique calibers and types from data
  const uniqueCalibers = [...new Set(ammunition.map(a => a.caliber))].filter(Boolean);
  const allCalibers = [...new Set([...COMMON_CALIBERS, ...uniqueCalibers])].sort();

  // Calculate total rounds
  const totalRounds = ammunition.reduce((sum, a) => sum + a.rounds, 0);

  // Group by caliber for summary
  const caliberSummary = ammunition.reduce((acc, a) => {
    if (!acc[a.caliber]) acc[a.caliber] = 0;
    acc[a.caliber] += a.rounds;
    return acc;
  }, {});

  return (
    <Layout
      title="Ammunition"
      showSearch
      onSearch={setSearchQuery}
      searchPlaceholder="Search caliber, brand..."
    >
      <div className="space-y-6" data-testid="ammunition-page">
        {/* Stats */}
        <div className="bg-card border border-border rounded-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold text-foreground font-['Chivo']">
                {totalRounds.toLocaleString()}
              </h2>
              <p className="text-sm text-muted-foreground uppercase tracking-widest">Total Rounds</p>
            </div>
            <Boxes className="w-10 h-10 text-accent" />
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={caliberFilter} onValueChange={setCaliberFilter}>
              <SelectTrigger className="w-36 rounded-sm" data-testid="filter-caliber">
                <SelectValue placeholder="Caliber" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Calibers</SelectItem>
                {allCalibers.map(caliber => (
                  <SelectItem key={caliber} value={caliber}>{caliber}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-36 rounded-sm" data-testid="filter-type">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {AMMO_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground"
              >
                Clear
              </Button>
            )}
          </div>

          <Button
            onClick={() => navigate('/ammunition/new')}
            className="bg-[#2E4A7D] hover:bg-[#3B5998] text-white rounded-sm btn-tactical"
            data-testid="add-ammo-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Ammunition
          </Button>
        </div>

        {/* Content */}
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="bg-card border border-border rounded-sm p-4 animate-pulse">
                <div className="h-6 bg-muted rounded w-1/4 mb-2" />
                <div className="h-4 bg-muted rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : ammunition.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Boxes className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Ammunition Found</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              {hasActiveFilters || searchQuery
                ? 'No ammunition matches your filters.'
                : 'Start tracking your ammunition inventory.'}
            </p>
            {!hasActiveFilters && !searchQuery && (
              <Button
                onClick={() => navigate('/ammunition/new')}
                className="bg-[#2E4A7D] hover:bg-[#3B5998] text-white rounded-sm btn-tactical"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Ammunition
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {ammunition.map((ammo, index) => (
              <div
                key={ammo.id}
                className="bg-card border border-border rounded-sm p-4 flex items-center gap-4 card-hover animate-fade-in"
                style={{ animationDelay: `${index * 0.03}s` }}
                data-testid={`ammo-card-${ammo.id}`}
              >
                {/* Using EntityThumbnail for object URL rendering */}
                <EntityThumbnail
                  imageFileIds={ammo.images}
                  alt=""
                  containerClassName="w-12 h-12 bg-accent/10 rounded-sm flex items-center justify-center flex-shrink-0 overflow-hidden"
                  className="w-full h-full object-cover"
                  fallback={<Boxes className="w-6 h-6 text-accent" />}
                />

                <div
                  className="flex-1 min-w-0 cursor-pointer"
                  onClick={() => navigate(`/ammunition/${ammo.id}`)}
                >
                  <h3 className="font-semibold text-foreground">
                    {ammo.caliber}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {ammo.brand || 'Unknown brand'}
                    {ammo.type && ` • ${ammo.type}`}
                    {ammo.grain && ` • ${ammo.grain}gr`}
                  </p>
                </div>

                {/* Round count and quick adjust */}
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => adjustRounds(ammo.id, -10)}
                    disabled={adjusting === ammo.id || ammo.rounds === 0}
                    className="rounded-sm h-8 w-8"
                    data-testid={`ammo-minus-${ammo.id}`}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  
                  <div className="text-center min-w-[60px]">
                    <span className="text-xl font-mono font-bold text-foreground">
                      {ammo.rounds.toLocaleString()}
                    </span>
                  </div>

                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => adjustRounds(ammo.id, 10)}
                    disabled={adjusting === ammo.id}
                    className="rounded-sm h-8 w-8"
                    data-testid={`ammo-plus-${ammo.id}`}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
